import { Box, Grid, Step, StepLabel, Stepper, Tooltip } from '@mui/material';
import LoadingButtonGeneral from '../../../components/general/buttons/loadingButtonGeneral';
import FormView from '../../../components/views/formView';
import { useChatAssistantForm } from './hook';
import { ChatAssistantData } from './data';
import { IdentityAndRole } from './identityAndRole';
import { ObjectiveAndResponseStrategy } from './objectiveAndResponseStrategy';
import { CommunicationArchitecture } from './communicationArchitecture';
import { DomainAreas } from './domainAreas';
import { StyleAndTonality } from './styleAndTonality';
import { SmartResponseFormat } from './smartResponseFormat';
import { KnowledgeBaseRules } from './knowledgeBaseRules';
import { EscalationAndCaseHandling } from './escalationAndCaseHandling';
import { DynamicConversationMemory } from './dynamicConversationMemory';
import { StrategicSummary } from './strategicSummary';
import { Dialog } from '../../../components/general/dialog/dialog';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StepIcon from '@mui/material/StepIcon';

export const ChatAssistantForm = () => {
    const {
        chatAssistant,
        steps,
        loading,
        activeStep,
        columns,
        rows,
        openApiKeyAlert,
        handleExitCreate,
        isApiKeyFounded,
        isSearched,
        openNotFoundApiKey,
        setOpenNotFoundApiKey,
        selectionModel,
        setSelectionModel,
        handleDeleteMultiple,
        formikChatAssistantData,
        formikIdentityAndRole,
        formikObjectiveAndResponseStrategy,
        formikCommunicationArchitecture,
        formikDomainAreas,
        formikStyleAndTonality,
        formikSmartResponseFormat,
        formikKnowledgeBaseRules,
        formikEscalationAndCaseHandling,
        formikDynamicConversationMemory,
        formikStrategicSummary,
        handleNextStep,
        completed,
        handleOnChangeSectionAlert,
        targetStepIndex,
        openChangeSectionAlert,
        setOpenChangeSectionAlert,
        projects,
        handleOnChangeFiles,
        isLastStep,
        handleBackStep,
        handleOnCloseApiKeyAlert,
        paginatedRows,
        paginationModel,
        handlePaginationChange,
        isSuperAdmin,
        connectToSharepoint,
        isSharepointAuth,
        openSharepointSelector,
        setIsSharepointAuth,
        setSharepointItems,
        setSharepointModalOpen,
        sharepointItems,
        sharepointModalOpen,
        users,
        isAssistantOwnerOrAdmin,
    } = useChatAssistantForm();

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <ChatAssistantData
                        data={chatAssistant}
                        formik={formikChatAssistantData}
                        columns={columns}
                        handleOnChange={handleOnChangeFiles}
                        projects={projects}
                        rows={rows}
                        isApiKeyFounded={isApiKeyFounded}
                        isSearched={isSearched}
                        paginatedRows={paginatedRows}
                        paginationModel={paginationModel}
                        handlePaginationChange={handlePaginationChange}
                        handleSelectionChange={(selectionModel) =>
                            setSelectionModel(selectionModel)
                        }
                        selectionModel={selectionModel}
                        handleDeleteMultiple={handleDeleteMultiple}
                        isSuperAdmin={isSuperAdmin}
                        connectToSharepoint={connectToSharepoint}
                        isSharepointAuth={isSharepointAuth}
                        openSharepointSelector={openSharepointSelector}
                        setIsSharepointAuth={setIsSharepointAuth}
                        setSharepointItems={setSharepointItems}
                        setSharepointModalOpen={setSharepointModalOpen}
                        sharepointItems={sharepointItems}
                        sharepointModalOpen={sharepointModalOpen}
                        users={users}
                        isAssistantOwnerOrAdmin={isAssistantOwnerOrAdmin}
                    />
                );
            case 1:
                return (
                    <IdentityAndRole
                        formikIdentityAndRole={formikIdentityAndRole}
                    />
                );
            case 2:
                return (
                    <ObjectiveAndResponseStrategy
                        formikObjectiveAndResponseStrategy={
                            formikObjectiveAndResponseStrategy
                        }
                    />
                );
            case 3:
                return (
                    <CommunicationArchitecture
                        formikCommunicationArchitecture={
                            formikCommunicationArchitecture
                        }
                    />
                );
            case 4:
                return <DomainAreas formikDomainAreas={formikDomainAreas} />;
            case 5:
                return (
                    <StyleAndTonality
                        formikStyleAndTonality={formikStyleAndTonality}
                    />
                );
            case 6:
                return (
                    <SmartResponseFormat
                        formikSmartResponseFormat={formikSmartResponseFormat}
                    />
                );
            case 7:
                return (
                    <KnowledgeBaseRules
                        formikKnowledgeBaseRules={formikKnowledgeBaseRules}
                    />
                );
            case 8:
                return (
                    <EscalationAndCaseHandling
                        formikEscalationAndCaseHandling={
                            formikEscalationAndCaseHandling
                        }
                    />
                );
            case 9:
                return (
                    <DynamicConversationMemory
                        formikDynamicConversationMemory={
                            formikDynamicConversationMemory
                        }
                    />
                );
            case 10:
                return (
                    <StrategicSummary
                        formikStrategicSummary={formikStrategicSummary}
                    />
                );
        }
    };

    return (
        <>
            <FormView
                title={
                    chatAssistant?.id
                        ? 'Editar asistente de chat'
                        : 'Crear asistente de chat'
                }
            />
            <Box pt={4} px={3} display="flex" flexDirection="column">
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={{ paddingBottom: '8px', overflowX: 'auto' }}
                    nonLinear
                >
                    {steps.map((label, index) => (
                        <Step key={label} completed={completed[index]}>
                            <StepLabel
                                onClick={
                                    activeStep === 0 && !chatAssistant
                                        ? undefined
                                        : () => handleNextStep(index)
                                }
                                sx={{
                                    cursor:
                                        activeStep === 0 && !chatAssistant
                                            ? 'not-allowed'
                                            : 'pointer',
                                }}
                                StepIconComponent={(props) => {
                                    if (props.completed) {
                                        return (
                                            <CheckCircleIcon
                                                sx={{
                                                    color: 'primary.dark',
                                                    fontSize: '1.8rem',
                                                }}
                                            />
                                        );
                                    }
                                    if (activeStep === 0 && !chatAssistant) {
                                        return (
                                            <Tooltip title="Completa la información inicial antes de continuar">
                                                <StepIcon {...props} />
                                            </Tooltip>
                                        );
                                    }
                                    return <StepIcon {...props} />;
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {renderStepContent()}
                <Grid item xs={12} mt={5} mb={10}>
                    <LoadingButtonGeneral
                        disabled={loading}
                        loading={loading}
                        title={isLastStep ? 'Guardar' : 'Siguiente'}
                        sx={{ mr: 1 }}
                        onClick={handleNextStep}
                    />
                    <LoadingButtonGeneral
                        disabled={loading}
                        title="Volver"
                        variant="outlined"
                        onClick={handleBackStep}
                    />
                </Grid>
            </Box>
            <Dialog
                title="Asistente creado"
                open={openApiKeyAlert}
                onClose={handleOnCloseApiKeyAlert}
                textBtn="Sí, continuar"
                textSecondBtn="No, salir"
                onClickSecondBtn={handleExitCreate}
            >
                El asistente básico se creó correctamente y se aplicó la API Key
                del proyecto actual.
                <br />
                ¿Desea seguir configurándolo?
            </Dialog>
            <Dialog
                title="API KEY no encontrada"
                open={openNotFoundApiKey}
                onClose={() => setOpenNotFoundApiKey(false)}
            >
                No se encontró una API KEY asociada al proyecto, debe ingresarla
                para continuar con la creación.
            </Dialog>
            <Dialog
                title="Campos incompletos"
                open={openChangeSectionAlert}
                onClose={() => setOpenChangeSectionAlert(false)}
                textBtn="Permanecer"
                textSecondBtn="Cambiar de sección"
                onClickSecondBtn={() =>
                    handleOnChangeSectionAlert(targetStepIndex as number)
                }
            >
                Algunos campos obligatorios no están completos
            </Dialog>
        </>
    );
};
